import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import {
  DollarSign, Users, CheckCircle, XCircle, TrendingUp, Calculator, FileText,
  Eye, Plus, Filter, Download, Search, ArrowRight, ArrowDown, ArrowLeft,
  Zap, Trash2, Loader2, Calendar, Upload, AlertCircle, Settings, Check,
  RefreshCw, Car, LogOut, Lock, Mail, Send, UserCheck, ChevronLeft
} from "lucide-react";
import { API, axios, formatIcelandicNumber, calculatePenaltyInterest, getLoanTypeLabel } from "@/utils/constants";
import { generatePaymentSchedule } from "@/utils/paymentSchedule";
import { useAuth } from "@/context/AuthContext";
import { fmtDateIS } from "@/utils/formatDate";

// Settings Form Component
export const SettingsForm = () => {
  const [vextir, setVextir] = useState("");
  const [greidslus, setGreidslus] = useState("");
  const [lantokugjald, setLantokugjald] = useState("");
  const [lantokugjaldPercentUnder1M, setLantokugjaldPercentUnder1M] = useState("");
  const [lantokugjaldPercentOver1M, setLantokugjaldPercentOver1M] = useState("");
  const [autoSendInvoice, setAutoSendInvoice] = useState(false);
  const [autoSendDaysBefore, setAutoSendDaysBefore] = useState("3");
  const [invoiceVisibilityDay, setInvoiceVisibilityDay] = useState("15");
  const [eindagiDaysAfter, setEindagiDaysAfter] = useState("4");
  const [requireInvoiceConfirmation, setRequireInvoiceConfirmation] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showAllFuturePayments, setShowAllFuturePayments] = useState(true);
  // Creditinfo Kennitala API
  const [creditinfoKennitalaApiUrl, setCreditinfoKennitalaApiUrl] = useState("https://api.creditinfo.is/subjectregistry/");
  const [creditinfoKennitalaUsername, setCreditinfoKennitalaUsername] = useState("ws.fondo");
  const [creditinfoKennitalaPassword, setCreditinfoKennitalaPassword] = useState("");
  const [creditinfoKennitalaClientId, setCreditinfoKennitalaClientId] = useState("WebAPI");
  const [creditinfoKennitalaSecret, setCreditinfoKennitalaSecret] = useState("");
  
  // Creditinfo Vehicle API
  const [creditinfoVehicleApiUrl, setCreditinfoVehicleApiUrl] = useState("https://api.creditinfo.is/vehicleregistrycore/");
  const [creditinfoVehicleUsername, setCreditinfoVehicleUsername] = useState("ws.fondo");
  const [creditinfoVehiclePassword, setCreditinfoVehiclePassword] = useState("");
  const [creditinfoVehicleClientId, setCreditinfoVehicleClientId] = useState("WebAPI");
  const [creditinfoVehicleSecret, setCreditinfoVehicleSecret] = useState("");
  
  // Legacy (kept for backward compatibility)
  const [creditinfoUsername, setCreditinfoUsername] = useState("");
  const [creditinfoPassword, setCreditinfoPassword] = useState("");
  const [creditinfoClient, setCreditinfoClient] = useState("WebAPI");
  const [creditinfoClientSecret, setCreditinfoClientSecret] = useState("");
  const [creditinfoMockMode, setCreditinfoMockMode] = useState(false);
  const [paydayClientId, setPaydayClientId] = useState("");
  const [paydayClientSecret, setPaydayClientSecret] = useState("");
  const [paydayApiUrl, setPaydayApiUrl] = useState("https://api.payday.is");
  const [paydayTokenUrl, setPaydayTokenUrl] = useState("https://api.payday.is/auth/token");
  const [m365TenantId, setM365TenantId] = useState("");
  const [m365ClientId, setM365ClientId] = useState("");
  const [m365ClientSecret, setM365ClientSecret] = useState("");
  const [mailFrom, setMailFrom] = useState("noreply@fondo.is");
  const [styrivextir, setStyrivextir] = useState("7.25");
  const [styrivextirUrl, setStyrivextirUrl] = useState("https://sedlabanki.is/gagnatorg/vextir/");
  const [styrivextirUpdated, setStyrivextirUpdated] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Interest rate notification state
  const [showRateChangeDialog, setShowRateChangeDialog] = useState(false);
  const [rateChangeStep, setRateChangeStep] = useState('preview'); // 'preview' | 'select'
  const [newInterestRate, setNewInterestRate] = useState("");
  const [originalVextir, setOriginalVextir] = useState(""); // track original value from DB
  const [effectiveDate, setEffectiveDate] = useState("");
  const [sendingRateNotification, setSendingRateNotification] = useState(false);
  const [rateNotificationProgress, setRateNotificationProgress] = useState({ sent: 0, total: 0, errors: [] });
  const [pendingRateChange, setPendingRateChange] = useState(null);
  const [applyingRateChange, setApplyingRateChange] = useState(false);
  const [activeCustomers, setActiveCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Calculate default effective date (30 days from today)
  useEffect(() => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    setEffectiveDate(thirtyDaysFromNow.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      // Use vextir if available, otherwise calculate from reibor + alag (legacy), otherwise default to 17.7
      const vextirValue = response.data.vextir 
        ? response.data.vextir.toString() 
        : (response.data.reibor && response.data.alag) 
          ? (response.data.reibor + response.data.alag).toString() 
          : "17.7";
      setVextir(vextirValue);
      setOriginalVextir(vextirValue);
      setGreidslus(response.data.greidslus?.toString() || "250");
      setLantokugjald(response.data.lantokugjald?.toString() || "0");
      setLantokugjaldPercentUnder1M(response.data.lantokugjald_percent_under_1m?.toString() || "0");
      setLantokugjaldPercentOver1M(response.data.lantokugjald_percent_over_1m?.toString() || "0");
      setAutoSendInvoice(response.data.auto_send_invoice || false);
      setAutoSendDaysBefore(response.data.auto_send_days_before?.toString() || "3");
      setInvoiceVisibilityDay(response.data.invoice_visibility_day?.toString() || "15");
      setEindagiDaysAfter(response.data.eindagi_days_after !== null && response.data.eindagi_days_after !== undefined ? response.data.eindagi_days_after.toString() : "4");
      setRequireInvoiceConfirmation(response.data.require_invoice_confirmation !== false); // Default true
      setShowAllFuturePayments(response.data.show_all_future_payments !== false);
      // Creditinfo Kennitala API
      setCreditinfoKennitalaApiUrl(response.data.creditinfo_kennitala_api_url || "https://api.creditinfo.is/subjectregistry/");
      setCreditinfoKennitalaUsername(response.data.creditinfo_kennitala_username || "ws.fondo");
      setCreditinfoKennitalaPassword(response.data.creditinfo_kennitala_password || "");
      setCreditinfoKennitalaClientId(response.data.creditinfo_kennitala_client_id || "WebAPI");
      setCreditinfoKennitalaSecret(response.data.creditinfo_kennitala_secret || "");
      // Creditinfo Vehicle API
      setCreditinfoVehicleApiUrl(response.data.creditinfo_vehicle_api_url || "https://api.creditinfo.is/vehicleregistrycore/");
      setCreditinfoVehicleUsername(response.data.creditinfo_vehicle_username || "ws.fondo");
      setCreditinfoVehiclePassword(response.data.creditinfo_vehicle_password || "");
      setCreditinfoVehicleClientId(response.data.creditinfo_vehicle_client_id || "WebAPI");
      setCreditinfoVehicleSecret(response.data.creditinfo_vehicle_secret || "");
      // Legacy
      setCreditinfoUsername(response.data.creditinfo_username || "");
      setCreditinfoPassword(response.data.creditinfo_password || "");
      setCreditinfoClient(response.data.creditinfo_client || "WebAPI");
      setCreditinfoClientSecret(response.data.creditinfo_client_secret || "");
      setCreditinfoMockMode(response.data.creditinfo_mock_mode || false);
      setPaydayClientId(response.data.payday_client_id || "");
      setPaydayClientSecret(response.data.payday_client_secret || "");
      setPaydayApiUrl(response.data.payday_api_url || "https://api.payday.is");
      setPaydayTokenUrl(response.data.payday_token_url || "https://api.payday.is/auth/token");
      setM365TenantId(response.data.m365_tenant_id || "");
      setM365ClientId(response.data.m365_client_id || "");
      setM365ClientSecret(response.data.m365_client_secret || "");
      setMailFrom(response.data.mail_from || "noreply@fondo.is");
      setStyrivextir(response.data.styrivextir?.toString() || "7.25");
      setStyrivextirUrl(response.data.styrivextir_url || "https://sedlabanki.is/gagnatorg/vextir/");
      setStyrivextirUpdated(response.data.styrivextir_updated || null);
      // Pending rate change
      setPendingRateChange(response.data.pending_rate_change || null);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleSave = async () => {
    if (!vextir || !greidslus) {
      toast.error("Vinsamlegast fylltu í alla reiti");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/settings`, {
        vextir: parseFloat(vextir),
        greidslus: parseFloat(greidslus),
        lantokugjald: parseFloat(lantokugjald) || 0,
        lantokugjald_percent_under_1m: parseFloat(lantokugjaldPercentUnder1M) || 0,
        lantokugjald_percent_over_1m: parseFloat(lantokugjaldPercentOver1M) || 0,
        auto_send_invoice: autoSendInvoice,
        auto_send_days_before: parseInt(autoSendDaysBefore) || 3,
        invoice_visibility_day: parseInt(invoiceVisibilityDay) || 15,
        eindagi_days_after: eindagiDaysAfter !== "" ? parseInt(eindagiDaysAfter) : 4,
        require_invoice_confirmation: requireInvoiceConfirmation,
        show_all_future_payments: showAllFuturePayments,
        // Creditinfo Kennitala API
        creditinfo_kennitala_api_url: creditinfoKennitalaApiUrl,
        creditinfo_kennitala_username: creditinfoKennitalaUsername,
        creditinfo_kennitala_password: creditinfoKennitalaPassword,
        creditinfo_kennitala_client_id: creditinfoKennitalaClientId,
        creditinfo_kennitala_secret: creditinfoKennitalaSecret,
        // Creditinfo Vehicle API
        creditinfo_vehicle_api_url: creditinfoVehicleApiUrl,
        creditinfo_vehicle_username: creditinfoVehicleUsername,
        creditinfo_vehicle_password: creditinfoVehiclePassword,
        creditinfo_vehicle_client_id: creditinfoVehicleClientId,
        creditinfo_vehicle_secret: creditinfoVehicleSecret,
        // Legacy
        creditinfo_username: creditinfoUsername,
        creditinfo_password: creditinfoPassword,
        creditinfo_client: creditinfoClient,
        creditinfo_client_secret: creditinfoClientSecret,
        creditinfo_mock_mode: creditinfoMockMode,
        payday_client_id: paydayClientId,
        payday_client_secret: paydayClientSecret,
        payday_api_url: paydayApiUrl,
        payday_token_url: paydayTokenUrl,
        m365_tenant_id: m365TenantId,
        m365_client_id: m365ClientId,
        m365_client_secret: m365ClientSecret,
        mail_from: mailFrom,
        styrivextir: parseFloat(styrivextir) || 7.25,
        styrivextir_url: styrivextirUrl
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      // Reload settings from backend to confirm save
      await fetchSettings();
      
      toast.success("✓ Stillingar vistaðar með góðum árangri!", { 
        duration: 4000,
        position: 'top-center',
        style: {
          background: '#10b981',
          color: '#fff',
          fontSize: '16px',
          fontWeight: 'bold',
          padding: '16px 24px'
        }
      });
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Villa við að uppfæra stillingar");
    } finally {
      setLoading(false);
    }
  };

  // Send interest rate change notifications to all active loans
  const fetchActiveCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const loansResponse = await axios.get(`${API}/loans`);
      const activeLoans = loansResponse.data.filter(
        loan => loan.status === 'disbursed' || loan.status === 'approved'
      );
      const loansWithEmail = activeLoans.filter(
        loan => loan.borrower_info?.email && loan.borrower_info.email.includes('@')
      );
      setActiveCustomers(loansWithEmail);
      setSelectedCustomers(loansWithEmail.map(l => l.id));
    } catch (error) {
      console.error("Error fetching active customers:", error);
      toast.error("Villa við að sækja viðskiptavini");
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Open rate change dialog when Vextir input changes
  const handleVextirBlur = (e) => {
    const newVal = e.target.value;
    if (newVal && newVal !== originalVextir && !pendingRateChange?.status) {
      setNewInterestRate(newVal);
      setRateChangeStep('preview');
      setRateNotificationProgress({ sent: 0, total: 0, errors: [] });
      setShowRateChangeDialog(true);
      fetchActiveCustomers();
    }
  };

  const handleSendRateNotifications = async (targetLoans) => {
    if (!newInterestRate) {
      toast.error("Vinsamlegast sláðu inn nýja vexti");
      return;
    }

    setSendingRateNotification(true);
    setRateNotificationProgress({ sent: 0, total: 0, errors: [] });

    try {
      const loansToSend = targetLoans || activeCustomers;

      setRateNotificationProgress(prev => ({ ...prev, total: loansToSend.length }));

      if (loansToSend.length === 0) {
        toast.error("Engir viðskiptavinir valdir");
        setSendingRateNotification(false);
        return;
      }

      const errors = [];
      let sentCount = 0;

      for (const loan of loansToSend) {
        try {
          await axios.post(`${API}/email/send-interest-change`, {
            to: loan.borrower_info.email,
            customer_name: loan.borrower_info.full_name,
            loan_amount: Math.round(loan.loan_amount).toLocaleString('is-IS'),
            old_rate: loan.interest_rate?.toString() || vextir,
            new_rate: newInterestRate,
            effective_date: fmtDateIS(effectiveDate)
          });
          sentCount++;
          setRateNotificationProgress(prev => ({ ...prev, sent: sentCount }));
        } catch (error) {
          errors.push(`${loan.borrower_info.full_name}: ${error.response?.data?.detail || error.message}`);
        }
      }

      setRateNotificationProgress(prev => ({ ...prev, errors }));

      if (sentCount > 0) {
        toast.success(`Tilkynningar sendar til ${sentCount} viðskiptavina`, { duration: 5000 });
        
        try {
          await axios.post(`${API}/settings/pending-rate-change`, {
            new_rate: parseFloat(newInterestRate),
            effective_date: effectiveDate,
            notification_date: new Date().toISOString(),
            emails_sent: sentCount
          });
          
          await fetchSettings();
        } catch (err) {
          console.error("Error storing pending rate change:", err);
        }
      }

      if (errors.length > 0) {
        toast.error(`${errors.length} villur komu upp við sendingu`, { duration: 5000 });
      }

      setTimeout(() => {
        setShowRateChangeDialog(false);
        setNewInterestRate("");
        setRateNotificationProgress({ sent: 0, total: 0, errors: [] });
        setRateChangeStep('preview');
      }, 3000);

    } catch (error) {
      console.error("Error sending rate notifications:", error);
      toast.error("Villa við að senda tilkynningar");
    } finally {
      setSendingRateNotification(false);
    }
  };

  // Apply pending rate change (after 30 days)
  const handleApplyRateChange = async () => {
    setApplyingRateChange(true);
    try {
      const response = await axios.post(`${API}/settings/apply-rate-change`);
      toast.success(response.data.message);
      await fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Villa við að virkja vaxtabreytingu");
    } finally {
      setApplyingRateChange(false);
    }
  };

  // Cancel pending rate change
  const handleCancelRateChange = async () => {
    try {
      await axios.delete(`${API}/settings/pending-rate-change`);
      toast.success("Vaxtabreyting hætt við");
      await fetchSettings();
    } catch (error) {
      toast.error("Villa við að hætta við vaxtabreytingu");
    }
  };

  // Calculate days remaining until rate change can be applied
  const getDaysUntilRateChange = () => {
    if (!pendingRateChange?.notification_date) return null;
    const notificationDate = new Date(pendingRateChange.notification_date);
    const today = new Date();
    const daysPassed = Math.floor((today - notificationDate) / (1000 * 60 * 60 * 24));
    return Math.max(0, 30 - daysPassed);
  };

  // vextir is now managed as a direct state variable

  return (
    <div className="space-y-4">
      {/* Vextir & Greiðslur Card */}
      <Card>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-600" />
            Vextir og greiðslur
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
      {/* Vextir Section - Compact */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            Vextir
          </label>
        </div>
        
        {/* Pending Rate Change Alert */}
        {pendingRateChange && pendingRateChange.status === 'pending' && (
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">Vaxtabreyting í bið</p>
                <p className="text-xs text-yellow-700 mt-1">
                  Vextir breytast úr <strong>{pendingRateChange.old_rate}%</strong> í <strong>{pendingRateChange.new_rate}%</strong>
                </p>
                <p className="text-xs text-yellow-700">
                  Tilkynning send: {fmtDateIS(pendingRateChange.notification_date)}
                </p>
                <p className="text-xs text-yellow-700">
                  Gildistaka: {fmtDateIS(pendingRateChange.effective_date)}
                </p>
                <p className="text-xs text-yellow-700">
                  Tölvupóstar sendir: {pendingRateChange.emails_sent}
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Ný lán nota nýju vextina ({pendingRateChange.new_rate}%) strax.
                </p>
                
                {getDaysUntilRateChange() > 0 ? (
                  <p className="text-xs font-medium text-yellow-800 mt-2">
                    {getDaysUntilRateChange()} dagar eftir — eldri lán uppfærast sjálfkrafa
                  </p>
                ) : (
                  <p className="text-xs font-medium text-green-700 mt-2">
                    <Check className="h-3 w-3 inline mr-1" />
                    Vaxtabreyting hefur tekið gildi sjálfkrafa
                  </p>
                )}
                
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-xs text-red-600 border-red-300 hover:bg-red-50 mt-2"
                  onClick={handleCancelRateChange}
                >
                  Hætta við vaxtabreytingu
                </Button>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Vextir (%)</label>
            <Input
              type="number"
              step="0.01"
              value={vextir}
              onChange={(e) => setVextir(e.target.value)}
              onBlur={handleVextirBlur}
              placeholder="17.7"
              className="h-6 text-xs"
              disabled={pendingRateChange && pendingRateChange.status === 'pending'}
              data-testid="vextir-input"
            />
            {pendingRateChange && pendingRateChange.status === 'pending' && (
              <p className="text-[9px] text-yellow-600 mt-1">Læst vegna vaxtabreytingar í bið</p>
            )}
          </div>
          {/* Samtals field removed - now using single Vextir field */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Greiðsluseðill (kr.)</label>
            <Input
              type="number"
              step="1"
              value={greidslus}
              onChange={(e) => setGreidslus(e.target.value)}
              placeholder="250"
              className="h-6 text-xs"
            />
          </div>
        </div>
      </div>

      {/* Interest Rate Change Notification Dialog */}
      <Dialog open={showRateChangeDialog} onOpenChange={(open) => {
        if (!open && !sendingRateNotification) {
          setShowRateChangeDialog(false);
          setVextir(originalVextir);
          setNewInterestRate("");
          setRateNotificationProgress({ sent: 0, total: 0, errors: [] });
          setRateChangeStep('preview');
        }
      }}>
        <DialogContent className="max-w-lg" data-testid="rate-change-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-600" />
              {rateChangeStep === 'preview' ? 'Vaxtabreyting – Tilkynning' : (
                <>
                  <button onClick={() => setRateChangeStep('preview')} className="text-blue-600 hover:underline flex items-center">
                    <ChevronLeft className="h-3 w-3" /> Til baka
                  </button>
                  <span className="mx-1">|</span> Velja viðskiptavini
                </>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">Vaxtabreytingu tilkynning</DialogDescription>
          </DialogHeader>

          {rateChangeStep === 'preview' && (
            <div className="space-y-3 py-1" data-testid="rate-change-preview">
              {/* Rate change summary */}
              <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3 border">
                <div className="flex-1 text-center">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide">Núverandi</p>
                  <p className="text-lg font-bold text-gray-800">{originalVextir}%</p>
                </div>
                <ArrowRight className="h-5 w-5 text-blue-500" />
                <div className="flex-1 text-center">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide">Nýir vextir</p>
                  <p className="text-lg font-bold text-blue-600">{newInterestRate}%</p>
                </div>
              </div>

              <div>
                <Label className="text-xs">Gildir frá</Label>
                <Input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="h-8 text-xs"
                  data-testid="rate-change-effective-date"
                />
              </div>

              {/* Email template preview */}
              <div className="border rounded-lg overflow-hidden" data-testid="email-template-preview">
                <div className="bg-blue-600 text-white px-4 py-2 text-center">
                  <p className="font-bold text-sm">FONDO ehf.</p>
                </div>
                <div className="bg-slate-50 px-4 py-3 space-y-2 text-xs text-gray-700">
                  <p className="font-semibold text-blue-700 text-sm">Tilkynning um breytingu á breytilegum vöxtum</p>
                  <p>Kæri/kæra <strong>[Nafn viðskiptavinar]</strong>,</p>
                  <p className="text-[11px] leading-relaxed">
                    Með vísan til lánasamninga þar sem kveðið er á um breytilega vexti, tilkynnist hér með að nafnvextir lána munu taka breytingum.
                  </p>
                  <div className="bg-white border-l-4 border-blue-600 p-3 rounded my-2">
                    <p><strong>Nýir nafnvextir:</strong> <span className="text-blue-600 font-bold text-sm">{newInterestRate}%</span></p>
                    <p><strong>Gildistaka:</strong> {effectiveDate ? fmtDateIS(effectiveDate) : '—'}</p>
                  </div>
                  <p className="text-[11px] leading-relaxed">Tilkynning þessi er send með minnst 30 daga fyrirvara.</p>
                  <p className="text-[10px] text-gray-500 mt-1 pt-1 border-t">Dagsetning tilkynningar: {fmtDateIS(new Date())}</p>
                </div>
                <div className="bg-blue-900 text-white px-4 py-1.5 text-center text-[10px]">
                  FONDO ehf. | Kennitala: 521216-1310 | info@fondo.is
                </div>
              </div>

              {/* Info */}
              <p className="text-[10px] text-gray-500 leading-relaxed">
                {activeCustomers.length > 0 
                  ? `${activeCustomers.length} viðskiptavinir með virk lán og gilt netfang.`
                  : loadingCustomers ? 'Sæki viðskiptavini...' : 'Engir viðskiptavinir fundust.'
                }
              </p>

              {/* Progress indicator */}
              {sendingRateNotification && (
                <div className="bg-gray-50 border rounded p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    <span className="text-xs font-medium">
                      Sendi tilkynningar... {rateNotificationProgress.sent}/{rateNotificationProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: rateNotificationProgress.total > 0 
                          ? `${(rateNotificationProgress.sent / rateNotificationProgress.total) * 100}%` 
                          : '0%' 
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Error list */}
              {rateNotificationProgress.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-2 max-h-24 overflow-y-auto">
                  <p className="text-xs font-medium text-red-700 mb-1">Villur:</p>
                  {rateNotificationProgress.errors.map((error, idx) => (
                    <p key={idx} className="text-xs text-red-600">{error}</p>
                  ))}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-col gap-2 pt-1">
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700 h-9 text-xs flex items-center justify-center gap-2"
                  onClick={() => handleSendRateNotifications(activeCustomers)}
                  disabled={sendingRateNotification || !newInterestRate || activeCustomers.length === 0}
                  data-testid="send-to-all-btn"
                >
                  {sendingRateNotification ? (
                    <><Loader2 className="h-3 w-3 animate-spin" /> Sendi...</>
                  ) : (
                    <><Send className="h-3 w-3" /> Geyma og senda til allra ({activeCustomers.length})</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-9 text-xs flex items-center justify-center gap-2"
                  onClick={() => setRateChangeStep('select')}
                  disabled={sendingRateNotification || activeCustomers.length === 0}
                  data-testid="choose-and-send-btn"
                >
                  <UserCheck className="h-3 w-3" /> Velja og senda
                </Button>
              </div>
            </div>
          )}

          {rateChangeStep === 'select' && (
            <div className="space-y-3 py-1" data-testid="rate-change-select">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-600">
                  {selectedCustomers.length} af {activeCustomers.length} valdir
                </p>
                <div className="flex gap-2">
                  <button 
                    className="text-[10px] text-blue-600 hover:underline"
                    onClick={() => setSelectedCustomers(activeCustomers.map(l => l.id))}
                  >
                    Velja alla
                  </button>
                  <button 
                    className="text-[10px] text-gray-500 hover:underline"
                    onClick={() => setSelectedCustomers([])}
                  >
                    Hreinsa
                  </button>
                </div>
              </div>

              <ScrollArea className="h-[280px] border rounded-lg">
                <div className="p-2 space-y-1">
                  {loadingCustomers ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                    </div>
                  ) : activeCustomers.map((loan) => (
                    <label 
                      key={loan.id} 
                      className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                        selectedCustomers.includes(loan.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                      }`}
                      data-testid={`customer-select-${loan.id}`}
                    >
                      <Checkbox
                        checked={selectedCustomers.includes(loan.id)}
                        onCheckedChange={(checked) => {
                          setSelectedCustomers(prev => 
                            checked ? [...prev, loan.id] : prev.filter(id => id !== loan.id)
                          );
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800 truncate">{loan.borrower_info?.full_name}</p>
                        <p className="text-[10px] text-gray-500 truncate">{loan.borrower_info?.email}</p>
                      </div>
                      <span className="text-[10px] text-gray-400">{loan.interest_rate || vextir}%</span>
                    </label>
                  ))}
                </div>
              </ScrollArea>

              {/* Progress indicator */}
              {sendingRateNotification && (
                <div className="bg-gray-50 border rounded p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    <span className="text-xs font-medium">
                      Sendi tilkynningar... {rateNotificationProgress.sent}/{rateNotificationProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: rateNotificationProgress.total > 0 
                          ? `${(rateNotificationProgress.sent / rateNotificationProgress.total) * 100}%` 
                          : '0%' 
                      }}
                    />
                  </div>
                </div>
              )}

              {rateNotificationProgress.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-2 max-h-20 overflow-y-auto">
                  <p className="text-xs font-medium text-red-700 mb-1">Villur:</p>
                  {rateNotificationProgress.errors.map((error, idx) => (
                    <p key={idx} className="text-xs text-red-600">{error}</p>
                  ))}
                </div>
              )}

              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 h-9 text-xs flex items-center justify-center gap-2"
                onClick={() => {
                  const selected = activeCustomers.filter(l => selectedCustomers.includes(l.id));
                  handleSendRateNotifications(selected);
                }}
                disabled={sendingRateNotification || selectedCustomers.length === 0}
                data-testid="send-to-selected-btn"
              >
                {sendingRateNotification ? (
                  <><Loader2 className="h-3 w-3 animate-spin" /> Sendi...</>
                ) : (
                  <><Send className="h-3 w-3" /> Senda til {selectedCustomers.length} viðskiptavina</>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

        </CardContent>
      </Card>

      {/* Lántökugjald Card */}
      <Card>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-green-600" />
            Lántökugjald
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
      <div>        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Undir 1M (%)
            </label>
            <Input
              type="number"
              step="0.1"
              value={lantokugjaldPercentUnder1M}
              onChange={(e) => setLantokugjaldPercentUnder1M(e.target.value)}
              placeholder="0"
              className="h-6 text-xs"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Yfir 1M (%)
            </label>
            <Input
              type="number"
              step="0.1"
              value={lantokugjaldPercentOver1M}
              onChange={(e) => setLantokugjaldPercentOver1M(e.target.value)}
              placeholder="0"
              className="h-6 text-xs"
            />
          </div>
          
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Lágmark (kr.)
            </label>
            <Input
              type="number"
              step="1"
              value={lantokugjald}
              onChange={(e) => setLantokugjald(e.target.value)}
              placeholder="0"
              className="h-6 text-xs"
            />
          </div>
        </div>
      </div>

        </CardContent>
      </Card>

      {/* Senda kröfu Card */}
      <Card>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Send className="h-4 w-4 text-purple-600" />
            Stillingar fyrir "Senda kröfu"
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">

        <div className="space-y-1.5">
          {/* Auto Send Invoice */}
          <div className="p-2 bg-gray-50 rounded">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  <label className="text-sm font-medium text-gray-700">Senda sjálfkrafa</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoSendInvoice}
                      onChange={(e) => setAutoSendInvoice(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span className="text-xs text-gray-600">{autoSendInvoice ? "Já" : "Nei"}</span>
                </div>
                {/* Show days input inline when auto-send is enabled */}
                {autoSendInvoice && (
                  <div className="flex items-center gap-2 mt-2 ml-1">
                    <label className="text-xs text-gray-600 whitespace-nowrap">Dagar fyrir gjalddaga:</label>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={autoSendDaysBefore}
                      onChange={(e) => setAutoSendDaysBefore(e.target.value)}
                      className="w-16 h-7 text-sm"
                      placeholder="3"
                    />
                  </div>
                )}
                
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={async () => {
                      if (window.confirm('Þetta mun keyra sjálfvirka sendingu strax fyrir prófun. Viltu halda áfram?')) {
                        try {
                          toast.loading("Keyri sjálfvirka sendingu...", { id: 'test-auto-send' });
                          const response = await axios.post(`${API}/admin/trigger-auto-send`);
                          toast.dismiss('test-auto-send');
                          if (response.data.success) {
                            toast.success("Sjálfvirk sending lokið! Athugaðu kerfiskladda fyrir upplýsingar.");
                          }
                        } catch (error) {
                          toast.dismiss('test-auto-send');
                          console.error("Error triggering auto-send:", error);
                          toast.error("Villa við að keyra sjálfvirka sendingu");
                        }
                      }
                    }}
                  >
                    Prófa núna (Test)
                  </Button>
                  <p className="text-[9px] text-gray-500 mt-1">
                    Keyrir strax fyrir prófun. Venjulega keyrir kl. 8:00 á hverjum degi.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Invoice Sending Settings */}
          <div className="p-3 bg-gray-50 rounded border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stillingar fyrir sendingu krafna
            </label>
            
            {/* Require Confirmation */}
            <div className="mb-2">
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={requireInvoiceConfirmation}
                  onChange={(e) => setRequireInvoiceConfirmation(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="font-medium">Staðfesta áður en send er krófa</span>
              </label>
              <p className="text-[10px] text-gray-500 mt-1 ml-5">
                Sýnir staðfestingarglugga með upplýsingum áður en krófa er send
              </p>
            </div>
            
            {/* Eindagi Days After Gjalddagi */}
            <div className="mt-2">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Eindagi dagar eftir gjalddaga:</label>
                <Input
                  type="number"
                  min="0"
                  max="30"
                  value={eindagiDaysAfter}
                  onChange={(e) => setEindagiDaysAfter(e.target.value)}
                  className="w-16 h-7 text-sm"
                  placeholder="4"
                />
                <span className="text-xs text-gray-500">dagar</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1 ml-1">
                0 dagar = Eindagi verður sami dagur og Gjalddagi
              </p>
            </div>
          </div>
        </div>

        </CardContent>
      </Card>

      {/* Creditinfo API Card */}
      <Card>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Search className="h-4 w-4 text-orange-600" />
            Creditinfo API
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
        {/* Kennitala Lookup API */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <label className="block text-sm font-semibold text-blue-900 mb-2">
            1. Kennitala Lookup API
          </label>
          
          <div className="grid grid-cols-1 gap-2 mb-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">API URL</label>
              <Input
                type="text"
                value={creditinfoKennitalaApiUrl}
                onChange={(e) => setCreditinfoKennitalaApiUrl(e.target.value)}
                placeholder="https://api.creditinfo.is/subjectregistry/"
                className="h-6 text-xs font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">User</label>
              <Input
                type="text"
                value={creditinfoKennitalaUsername}
                onChange={(e) => setCreditinfoKennitalaUsername(e.target.value)}
                placeholder="ws.fondo"
                className="h-6 text-xs"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Password</label>
              <Input
                type="password"
                value={creditinfoKennitalaPassword}
                onChange={(e) => setCreditinfoKennitalaPassword(e.target.value)}
                placeholder="#J130686os"
                className="h-6 text-xs"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">ClientID</label>
              <Input
                type="text"
                value={creditinfoKennitalaClientId}
                onChange={(e) => setCreditinfoKennitalaClientId(e.target.value)}
                placeholder="WebAPI"
                className="h-6 text-xs"
              />
            </div>
            
            <div>
              <label className="block text-xs text-gray-600 mb-1">Secret</label>
              <Input
                type="password"
                value={creditinfoKennitalaSecret}
                onChange={(e) => setCreditinfoKennitalaSecret(e.target.value)}
                placeholder="sfagoij4w2t524t5jO09vnbcvknm5ry7uiw53yO"
                className="h-6 text-xs font-mono"
              />
            </div>
          </div>
        </div>

        {/* Vehicle Lookup API */}
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <label className="block text-sm font-semibold text-green-900 mb-2">
            2. Vehicle Lookup API
          </label>
          
          <div className="grid grid-cols-1 gap-2 mb-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">API URL</label>
              <Input
                type="text"
                value={creditinfoVehicleApiUrl}
                onChange={(e) => setCreditinfoVehicleApiUrl(e.target.value)}
                placeholder="https://api.creditinfo.is/vehicleregistrycore/"
                className="h-6 text-xs font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">User</label>
              <Input
                type="text"
                value={creditinfoVehicleUsername}
                onChange={(e) => setCreditinfoVehicleUsername(e.target.value)}
                placeholder="ws.fondo"
                className="h-6 text-xs"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Password</label>
              <Input
                type="password"
                value={creditinfoVehiclePassword}
                onChange={(e) => setCreditinfoVehiclePassword(e.target.value)}
                placeholder="#J130686os"
                className="h-6 text-xs"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">ClientID</label>
              <Input
                type="text"
                value={creditinfoVehicleClientId}
                onChange={(e) => setCreditinfoVehicleClientId(e.target.value)}
                placeholder="WebAPI"
                className="h-6 text-xs"
              />
            </div>
            
            <div>
              <label className="block text-xs text-gray-600 mb-1">Secret</label>
              <Input
                type="password"
                value={creditinfoVehicleSecret}
                onChange={(e) => setCreditinfoVehicleSecret(e.target.value)}
                placeholder="sfagoij4w2t524t5jO09vnbcvknm5ry7uiw53yO"
                className="h-6 text-xs font-mono"
              />
            </div>
          </div>
        </div>

        {/* Mock Mode Toggle */}
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="block text-sm font-medium text-amber-900 mb-1">
                Mock Mode (Próf hamur)
              </label>
              <p className="text-xs text-amber-700">
                Nota sýnigögn í stað raunverulegrar Creditinfo API tengingar. Notið þetta ef API auðkenning er ekki tiltæk.
              </p>
            </div>
            <Switch
              checked={creditinfoMockMode}
              onCheckedChange={setCreditinfoMockMode}
              className="ml-4"
            />
          </div>
          {creditinfoMockMode && (
            <div className="mt-2 text-xs text-amber-800 font-medium">
              ⚠️ MOCK MODE VIRKT - Sýnigögn verða notuð fyrir allar Creditinfo fyrirspurnir
            </div>
          )}
        </div>

        </CardContent>
      </Card>

      {/* Payday API Card */}
      <Card>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <FileText className="h-4 w-4 text-teal-600" />
            Payday API
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Client ID</label>
            <Input
              type="text"
              value={paydayClientId}
              onChange={(e) => setPaydayClientId(e.target.value)}
              placeholder="Client ID"
              className="h-6 text-xs"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Client Secret</label>
            <Input
              type="password"
              value={paydayClientSecret}
              onChange={(e) => setPaydayClientSecret(e.target.value)}
              placeholder="••••••••"
              className="h-6 text-xs"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">API URL</label>
            <Input
              type="text"
              value={paydayApiUrl}
              onChange={(e) => setPaydayApiUrl(e.target.value)}
              placeholder="https://api.payday.is"
              className="h-6 text-xs"
            />
          </div>
          
          <div>
            <label className="block text-xs text-gray-600 mb-1">Token URL</label>
            <Input
              type="text"
              value={paydayTokenUrl}
              onChange={(e) => setPaydayTokenUrl(e.target.value)}
              placeholder="https://api.payday.is/auth/token"
              className="h-6 text-xs"
            />
          </div>
        </div>

        </CardContent>
      </Card>

      {/* Microsoft 365 Card */}
      <Card>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Mail className="h-4 w-4 text-blue-600" />
            Tölvupóstur (Microsoft 365)
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Tenant ID</label>
            <Input
              type="text"
              value={m365TenantId}
              onChange={(e) => setM365TenantId(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="h-6 text-xs"
              data-testid="m365-tenant-id"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Client ID</label>
            <Input
              type="text"
              value={m365ClientId}
              onChange={(e) => setM365ClientId(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="h-6 text-xs"
              data-testid="m365-client-id"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Client Secret</label>
            <Input
              type="password"
              value={m365ClientSecret}
              onChange={(e) => setM365ClientSecret(e.target.value)}
              placeholder="Client secret..."
              className="h-6 text-xs"
              data-testid="m365-client-secret"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Sendandi (Mail From)</label>
            <Input
              type="email"
              value={mailFrom}
              onChange={(e) => setMailFrom(e.target.value)}
              placeholder="noreply@fondo.is"
              className="h-6 text-xs"
              data-testid="mail-from"
            />
          </div>
        </div>

        </CardContent>
      </Card>

      {/* Seðlabanki Card */}
      <Card>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-amber-600" />
            Seðlabanki Íslands – Meginvextir
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Meginvextir (%)</label>
            <Input
              type="number"
              step="0.01"
              value={styrivextir}
              onChange={(e) => setStyrivextir(e.target.value)}
              className="h-6 text-xs"
              data-testid="styrivextir-input"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Hámarks ÁHK (35% + meginvextir)</label>
            <Input
              type="text"
              value={`${(35 + (parseFloat(styrivextir) || 0)).toFixed(2)}%`}
              disabled
              className="h-6 text-xs bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Síðast uppfært</label>
            <Input
              type="text"
              value={styrivextirUpdated ? fmtDateIS(styrivextirUpdated) : 'Ekki enn'}
              disabled
              className="h-6 text-xs bg-gray-100"
            />
          </div>
        </div>
        <div className="mt-2">
          <label className="block text-xs text-gray-600 mb-1">Vefslóð Seðlabanka</label>
          <Input
            type="url"
            value={styrivextirUrl}
            onChange={(e) => setStyrivextirUrl(e.target.value)}
            className="h-6 text-xs"
            data-testid="styrivextir-url"
          />
          <p className="text-[9px] text-gray-400 mt-1">Sótt sjálfkrafa vikulega á mánudögum kl. 10:00</p>
        </div>

        </CardContent>
      </Card>

      <div className="flex justify-end gap-2 items-center">
        {saveSuccess && (
          <span className="text-green-600 font-semibold text-sm animate-pulse">
            Stillingar vistaðar
          </span>
        )}
        <Button onClick={handleSave} disabled={loading} className="bg-green-600 hover:bg-green-700 h-7 text-xs px-3">
          {loading ? "Vistar..." : "Vista Stillingar"}
        </Button>
      </div>
    </div>
  );
};

